import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import Cropper from 'react-easy-crop'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import styled, { css } from 'styled-components'
import bytes from 'bytes'
import * as Sentry from '@sentry/react'
import { selectUser } from '../../../modules/user/selectors'
import { getDimensions } from '../../../shared_components/Missions/Types/Upload'
import { theseus2 } from '../../../api/theseus'
import EmbeddableModal, {
  stopPropagation,
} from '../../../shared_components/EmbeddableModal'

import { Box } from '../../../shared_components/UI/Box'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import useWeb3 from '../../../hooks/useWeb3'
import useIsWalletConnected from '../../../shared_components/Header/useIsWalletConnected'
import ThemedButton from '../../../shared_components/ThemedButton'
import { ImageUploaderCardBox } from '../../../shared_components/Library/Card'
import { Span } from '../../../shared_components/Library/Typeography'
import XClose from '../../../shared_components/Icons/XClose'

export type ImageWithDimensions = {
  url: string
  width: number
  height: number
}

export function ImageCropModal({
  imgSrc,
  setImageToResize,
  setResizedImage,
  desiredHeight = 1200,
  desiredWidth = 1200,
}: {
  imgSrc: string
  setImageToResize: (values: ImageWithDimensions | null) => void
  setResizedImage: (values: ImageWithDimensions | null) => void
  desiredHeight?: number
  desiredWidth?: number
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [isImageDark, setIsImageDark] = useState(false)

  const onCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }

  const showCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(
        imgSrc,
        croppedAreaPixels,
        rotation,
      )

      setResizedImage({
        url: croppedImage as string,
        width: croppedAreaPixels.width,
        height: croppedAreaPixels.height,
      })
      setImageToResize(null)
    } catch (e) {
      console.error(e)
    }
  }

  const onClose = () => {
    setImageToResize(null)
  }

  const checkImage = useCallback(async () => {
    try {
      const imageType = await isImageLightOrDark(imgSrc)
      setIsImageDark(imageType === 'dark')
    } catch (e) {
      console.error(e)
    }
  }, [imgSrc])

  useEffect(() => {
    checkImage()
  }, [checkImage, imgSrc])

  if (!imgSrc) return <></>

  return (
    <EmbeddableModal
      isVisible={true}
      dismiss={onClose}
      hideBolt
      minWidth={1200}
    >
      <CloseButton>
        <ThemedButton onClick={onClose} text={'Close'} />
      </CloseButton>
      <Confirm>
        <ThemedButton
          $styleType="primary"
          onClick={showCroppedImage}
          text={'Crop'}
        />
      </Confirm>
      <CropperContainer isImageDark={isImageDark}>
        <Cropper
          image={imgSrc}
          crop={crop}
          rotation={rotation}
          zoom={zoom}
          aspect={desiredWidth / desiredHeight}
          onCropChange={setCrop}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
        />
      </CropperContainer>
    </EmbeddableModal>
  )
}

const CloseButton = styled.div`
  position: absolute;

  top: 1rem;
  left: 1rem;
  z-index: 1000;
  max-width: 200px;
`

const Confirm = styled.div`
  position: absolute;

  top: 1rem;
  right: 1rem;
  z-index: 1000;
  max-width: 170px;
`

function createImage(url: string) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.setAttribute('crossOrigin', 'anonymous')
    image.src = url
  })
}

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation)

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  }
}

export function isLightHexCode(hexCode: string) {
  const rgb = hexToRgb(hexCode)
  if (!rgb) return false
  const { r, g, b } = rgb
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 155
}

function hexToRgb(hex: string) {
  if (hex.length === 4) {
    hex = hex.replace(/([^#])/g, '$1$1')
  }
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex) as any
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

/**
 * Function to determine if an image is mostly light or dark.
 * @param {HTMLImageElement} imgElement The image element to analyze.
 * @returns {Promise<string>} A promise that resolves to either "light" or "dark".
 */
async function isImageLightOrDark(imgSrc: string) {
  return new Promise(async (resolve, reject) => {
    const imgElement: any = await createImage(imgSrc)
    // Create a canvas element and set its dimensions
    const canvas = document.createElement('canvas')
    canvas.width = imgElement.width
    canvas.height = imgElement.height

    // Get the 2D rendering context
    const ctx = canvas.getContext('2d')

    if (!ctx) return reject('dark')
    // Draw the image onto the canvas
    ctx.drawImage(imgElement, 0, 0)

    // Fetch the pixel data from the canvas
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data

    let lightPixels = 0
    let darkPixels = 0

    // Loop through the pixel data (r, g, b, a)
    for (let i = 0; i < imageData.length; i += 4) {
      const r = imageData[i]
      const g = imageData[i + 1]
      const b = imageData[i + 2]

      // Calculate the luminance of the pixel
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b

      // Classify as either light or dark
      if (luminance > 128) {
        lightPixels++
      } else {
        darkPixels++
      }
    }

    // Determine if the image is mostly light or dark
    if (lightPixels > darkPixels) {
      resolve('light')
    } else {
      resolve('dark')
    }
  })
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
async function getCroppedImg(
  imageSrc: string,
  pixelCrop: any,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
) {
  const image: any = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return null
  }

  const rotRad = getRadianAngle(rotation)

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  )

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth
  canvas.height = bBoxHeight

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
  ctx.rotate(rotRad)
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1)
  ctx.translate(-image.width / 2, -image.height / 2)

  // draw rotated image
  ctx.drawImage(image, 0, 0)

  const croppedCanvas = document.createElement('canvas')

  const croppedCtx = croppedCanvas.getContext('2d')

  if (!croppedCtx) {
    return null
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width
  croppedCanvas.height = pixelCrop.height

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  )

  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((file) => {
      resolve(URL.createObjectURL(file as Blob))
    })
  })
}

export function useResizedImageUpload(onImageUpload: (file: File) => void) {
  const [imageToResize, setImageToResize] =
    useState<ImageWithDimensions | null>(null)
  const [isResizingImage, setIsResizingImage] = useState(false)

  const handleResizedImageUpload = useCallback(
    async (resizedImage: ImageWithDimensions | null) => {
      if (!resizedImage) return
      try {
        const file = await urlToFile(
          resizedImage.url,
          'resized-image.webp',
          'image/webp',
        )

        if (!file) throw new Error('Error converting image to file')
        onImageUpload(file)
      } catch (error) {
        console.error(error)
        toast.error('Error resizing image')
      }
    },
    [onImageUpload],
  )

  const setResizedImage = useCallback(
    (resizedImage: ImageWithDimensions | null) => {
      handleResizedImageUpload(resizedImage)
    },
    [handleResizedImageUpload],
  )

  return {
    imageToResize,
    setImageToResize,
    isResizingImage,
    setIsResizingImage,
    setResizedImage,
  }
}

export function getAspectRatio(width: number, height: number) {
  const gcd = (a: number, b: number): any => (b ? gcd(b, a % b) : a)
  const divisor = gcd(width, height)
  return `${width / divisor}:${height / divisor}`
}

export function getImageDimensions(file: File) {
  return new Promise<{ width: number; height: number }>((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      })
    }

    img.src = URL.createObjectURL(file)
  })
}

export async function urlToFile(
  url: string,
  filename: string,
  mimeType: string,
) {
  try {
    const res = await fetch(url, {
      mode: 'no-cors',
    })
    const buf = await res.arrayBuffer()
    return new File([buf], filename, { type: mimeType })
  } catch (error) {
    console.error(error, 'urlToFile')
  }
}

const CropperContainer = styled(Box)<{ isImageDark: boolean }>`
  height: calc(80vh - 4rem);

  max-height: calc(800px - 4rem);
  background: ${({ theme }) => theme.colors.cardBackgroundSolid};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    height: calc(60vh - 4rem);
    max-height: calc(400px - 6rem);
  }
`
